import { PersonLane } from '@app/modules/persons/appearances/types';
import { Box, Stack, Typography } from '@mui/material';
import { PersonLaneAvatar } from './PersonLaneAvatar';
import { usePersonLaneIdentity } from '../hooks/usePersonLaneIdentity';

type PersonLaneIdentityProps = {
  lane: PersonLane;
  /** Avatar outer size in px. */
  avatarPx: number;
  /** Colored ring width around the avatar. */
  borderWidth?: number;
  /** When true, unknown lanes use a dashed border. */
  dashedWhenUnknown?: boolean;
};

/**
 * Compact person identity block for popovers and cast hover cards.
 *
 * Shows {@link PersonLaneAvatar} beside the resolved display name and optional
 * profession. Used by {@link PersonInfo} and cast-cluster hover content.
 */
export function PersonLaneIdentity({
  lane,
  avatarPx,
  borderWidth = 2,
  dashedWhenUnknown = false,
}: PersonLaneIdentityProps) {
  const { nameNode, profession } = usePersonLaneIdentity(lane);

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <PersonLaneAvatar
        lane={lane}
        sizePx={avatarPx}
        borderWidth={borderWidth}
        dashedWhenUnknown={dashedWhenUnknown}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {nameNode}
        </Typography>
        {profession && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {profession}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
